import type { InstantSearch } from "instantsearch.js";
import {
    rangeFiltersState,
    subscribeRangeFiltersState,
    updateRangeFilter,
    type RangeFiltersState,
    type RangeValue,
} from "@/services/algoliaRouting.blade";
import { debounce } from "./helpers";

type RangeAttribute = keyof Omit<RangeFiltersState, "specification">;

function resolveFacetContainers(selector: string | string[]): HTMLElement[] {
    const selectors = Array.isArray(selector) ? selector : [selector];
    return selectors.flatMap((sel) => {
        const el = document.querySelector<HTMLElement>(sel);
        return el ? [el] : [];
    });
}

function readRangeConfig(container: HTMLElement) {
    return {
        minLabel: container.dataset.minLabel ?? "Minimum",
        maxLabel: container.dataset.maxLabel ?? "Maximum",
        unit: container.dataset.unit ?? "",
        showMin: container.dataset.showMin !== "false",
        showMax: container.dataset.showMax !== "false",
    };
}

function buildRangeHtml(config: ReturnType<typeof readRangeConfig>): string {
    const { minLabel, maxLabel, unit, showMin, showMax } = config;
    const cols = showMin && showMax ? "1fr 0.1fr 1fr" : "1fr";
    let html = `<div class="non-refining-range__wrapper" style="grid-template-columns:${cols}">`;

    if (showMin) {
        html += `
                        <div class="input-inner-label">
                            <span class="input-inner-label__unit">${unit}</span>
                            <label class="input-inner-label__label">${minLabel}</label>
                            <input class="input-inner-label__input" type="text" data-range="min" />
                        </div>`;
    }

    if (showMin && showMax) {
        html += `<div class="non-refining-range__separation"></div>`;
    }

    if (showMax) {
        html += `
                        <div class="input-inner-label">
                            <span class="input-inner-label__unit">${unit}</span>
                            <label class="input-inner-label__label">${maxLabel}</label>
                            <input class="input-inner-label__input" type="text" data-range="max" />
                        </div>`;
    }

    html += `</div>`;
    return html;
}

function parseRangeFromContainer(
    container: HTMLElement,
    showMin: boolean,
    showMax: boolean,
): RangeValue | null {
    const minInput = container.querySelector(
        '[data-range="min"]',
    ) as HTMLInputElement | null;
    const maxInput = container.querySelector(
        '[data-range="max"]',
    ) as HTMLInputElement | null;

    const hasMin =
        showMin && minInput?.value !== undefined && minInput.value !== "";
    const hasMax =
        showMax && maxInput?.value !== undefined && maxInput.value !== "";

    const parsedMin = hasMin ? Number(minInput!.value) : undefined;
    const parsedMax = hasMax ? Number(maxInput!.value) : undefined;

    if (parsedMin !== undefined && isNaN(parsedMin)) return null;
    if (parsedMax !== undefined && isNaN(parsedMax)) return null;
    if (
        parsedMin !== undefined &&
        parsedMax !== undefined &&
        parsedMin > parsedMax
    ) {
        return null;
    }

    return { min: parsedMin, max: parsedMax };
}

function syncRangeInputs(
    containers: HTMLElement[],
    range: RangeValue,
    showMin: boolean,
    showMax: boolean,
): void {
    for (const container of containers) {
        const minInput = container.querySelector(
            '[data-range="min"]',
        ) as HTMLInputElement | null;
        const maxInput = container.querySelector(
            '[data-range="max"]',
        ) as HTMLInputElement | null;

        if (showMin && minInput && document.activeElement !== minInput) {
            minInput.value = range.min !== undefined ? String(range.min) : "";
        }
        if (showMax && maxInput && document.activeElement !== maxInput) {
            maxInput.value = range.max !== undefined ? String(range.max) : "";
        }
    }
}

function bindRangeInputs(
    containers: HTMLElement[],
    attribute: RangeAttribute,
    showMin: boolean,
    showMax: boolean,
): void {
    const debouncedUpdate = debounce((sourceContainer: HTMLElement) => {
        const value = parseRangeFromContainer(
            sourceContainer,
            showMin,
            showMax,
        );
        if (value === null) return;
        updateRangeFilter(attribute, value);
    }, 500);

    for (const container of containers) {
        container.querySelectorAll("input").forEach((input) => {
            input.addEventListener("input", () => {
                debouncedUpdate(container);
            });

            input.addEventListener("keydown", (e) => {
                const controlKeys = [
                    "Backspace",
                    "Tab",
                    "Enter",
                    "ArrowLeft",
                    "ArrowRight",
                    "ArrowUp",
                    "ArrowDown",
                    "Delete",
                ];
                if (!controlKeys.includes(e.key) && isNaN(Number(e.key))) {
                    e.preventDefault();
                }
            });
        });
    }
}

/**
 * Non-refining range inputs (Vue: RangeFilterWidget + NonRefiningRange).
 * Filters via configure.filters + rangeFiltersState, not connectRange.
 */
export function createCustomRangeWidget(
    containerSelector: string | string[],
    attribute: RangeAttribute,
    search: InstantSearch,
) {
    const containers = resolveFacetContainers(containerSelector);
    if (!containers.length) return null;

    const config = readRangeConfig(containers[0]);
    const rangeHtml = buildRangeHtml(config);

    for (const container of containers) {
        container.innerHTML = rangeHtml;
    }

    bindRangeInputs(containers, attribute, config.showMin, config.showMax);

    const syncUi = () => {
        const range = rangeFiltersState.value[attribute] ?? {};
        syncRangeInputs(containers, range, config.showMin, config.showMax);
    };

    syncUi();

    const stopWatch = subscribeRangeFiltersState(syncUi);

    const onRender = () => syncUi();
    search.on("render", onRender);

    return {
        $$type: "custom.nonRefiningRange",
        $$widgetType: "custom.nonRefiningRange",
        init() {},
        render() {
            syncUi();
        },
        getWidgetUiState(uiState: Record<string, unknown>) {
            // Non-refining ranges flow through configure.filters / rangeFiltersState,
            // not a uiState slice. Pass the accumulated uiState through untouched —
            // returning a fresh object here wipes earlier widgets' contributions
            // (query, refinementList…) during InstantSearch's getUiState reduce.
            return uiState;
        },
        dispose() {
            search.removeListener("render", onRender);
            stopWatch();
        },
    };
}
